<template>
  <div class="manual-category">
    <b-row>

      <!-- Search / library -->
      <b-col md="6" class="mb-3 mb-md-0">
        <div class="mc-pane">
          <div class="mc-pane__header">
            <font-awesome-icon icon="search" class="mc-pane__header-icon"/>
            <span class="mc-pane__title">Search Library</span>
          </div>

          <b-input-group class="mc-search">
            <b-form-input
              v-model="searchInput"
              placeholder="Type to search by title…"
              autocomplete="off"
            />
            <b-input-group-append>
              <b-button :disabled="!searchInput" variant="outline-secondary" @click="searchInput = ''">
                <font-awesome-icon icon="times"/>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div class="mc-list-wrap">
            <div v-if="loading" class="mc-state">
              <b-spinner small class="mb-2"/>
              <span>Searching…</span>
            </div>

            <div v-else-if="error" class="mc-state mc-state--error">
              <font-awesome-icon icon="exclamation-circle" class="mc-state__icon"/>
              <span>Something went wrong.</span>
              <b-button size="sm" variant="outline-secondary" class="mt-2" @click="search(searchInput.trim())">Retry</b-button>
            </div>

            <div v-else-if="!searchInput.trim()" class="mc-state">
              <font-awesome-icon icon="search" class="mc-state__icon"/>
              <span>Start typing to find items to add.</span>
            </div>

            <div v-else-if="!srcItems.length" class="mc-state">
              <font-awesome-icon icon="ban" class="mc-state__icon"/>
              <span>No items match “{{ searchInput.trim() }}”.</span>
            </div>

            <draggable
              v-else
              class="mc-list"
              :list="srcItems"
              :group="{ name: 'mediaItems', pull: true, put: false }"
              :sort="false"
            >
              <div
                v-for="item in srcItems"
                :key="item.id"
                class="mc-item mc-item--src"
                :class="{ 'mc-item--added': isAdded(item.id) }"
                @click="addItem(item)"
              >
                <font-awesome-icon :icon="typeIcon(item.type)" class="mc-item__type-icon"/>
                <div class="mc-item__body">
                  <div class="mc-item__title">{{ itemTitle(item) }}</div>
                  <div class="mc-item__type">{{ prettyType(item.type) }}</div>
                </div>
                <span class="mc-item__action" :class="{ 'mc-item__action--added': isAdded(item.id) }">
                  <template v-if="isAdded(item.id)"><font-awesome-icon icon="check"/> Added</template>
                  <template v-else><font-awesome-icon icon="plus-circle"/> Add</template>
                </span>
              </div>
            </draggable>
          </div>
        </div>
      </b-col>

      <!-- Category contents -->
      <b-col md="6">
        <div class="mc-pane">
          <div class="mc-pane__header">
            <font-awesome-icon icon="list-ol" class="mc-pane__header-icon"/>
            <span class="mc-pane__title">In this Category</span>
            <b-badge pill class="mc-count">{{ dstItems.length }}</b-badge>
          </div>

          <div class="mc-list-wrap">
            <draggable
              class="mc-list dragArea"
              :class="{ 'mc-list--empty': !dstItems.length }"
              :list="dstItems"
              group="mediaItems"
              handle=".mc-item__handle"
              @change="onReorder"
            >
              <div v-for="(item, ndx) in dstItems" :key="item.id" class="mc-item mc-item--dst">
                <span class="mc-item__handle" title="Drag to reorder">
                  <font-awesome-icon icon="grip-vertical"/>
                </span>
                <span class="mc-item__pos">{{ ndx + 1 }}</span>
                <div class="mc-item__body">
                  <div class="mc-item__title">{{ itemTitle(item) }}</div>
                  <div class="mc-item__type">{{ prettyType(item.type) }}</div>
                </div>
                <b-button variant="link" class="mc-item__remove" title="Remove" @click.stop="removeItem(item.id)">
                  <font-awesome-icon icon="trash-alt"/>
                </b-button>
              </div>

              <!-- Empty state lives inside the draggable so items can be dropped onto it -->
              <div v-if="!dstItems.length" slot="header" class="mc-state mc-state--drop">
                <font-awesome-icon icon="layer-group" class="mc-state__icon"/>
                <span>No items yet.</span>
                <small>Click an item on the left to add it, or drag it here.</small>
              </div>
            </draggable>
          </div>
        </div>
      </b-col>

    </b-row>
  </div>
</template>

<script>
import Draggable from "vuedraggable"

const TYPE_ICONS = {
  video: "video",
  episode: "video",
  series: "tv",
  audio: "play-circle",
  album_track: "play-circle",
  music_album: "play-circle",
  podcast: "rss",
  podcast_episode: "rss",
  book: "book",
  magazine: "book",
  photo: "images",
  external: "external-link-alt",
  brand: "layer-group",
  page: "layer-group",
  artist_page: "layer-group",
  playlist_page: "layer-group"
}

export default {
  name: 'EditManualCategory',

  components: {
    Draggable
  },

  data: function() {
    return {
      searchInput: '',
      allowedMediaTypes: [],
      srcItems: [],
      dstItems: [],
      loading: false,
      error: false,
      searchSeq: 0
    };
  },

  props: {
    lang: String,
    items: Array,
    format: String,
    assetFormat: String
  },

  computed: {
    addedIds() {
      return new Set(this.dstItems.map(item => item.id))
    }
  },

  watch: {
    assetFormat(val) {
      this.setAllowedMediaTypes(val)
    },

    searchInput(val) {
      const query = (val || '').trim()

      if(query.length) {
        this.error = false
        this.loading = true
        this.debouncedSearch(query)
      }
      else {
        // Cancel any pending/in-flight request and reset the result list.
        this.debouncedSearch.cancel()
        this.searchSeq++
        this.srcItems = []
        this.loading = false
        this.error = false
      }
    }
  },

  methods: {
    setAllowedMediaTypes(assetFormat) {
      switch(assetFormat) {
        case 'audio':
          this.allowedMediaTypes = ['album_track', 'audio', 'podcast_episode']
          break;

        case 'video':
          this.allowedMediaTypes = ['episode', 'video']
          break;

        default:
          this.allowedMediaTypes = ['audio', 'video', 'series', 'magazine', 'music_album', 'podcast', 'book', 'brand', 'page', 'external', 'photo', 'artist_page', 'playlist_page']
          break;
      }
    },

    search(query) {
      query = (query || '').trim()

      if(!query.length) {
        return
      }

      const seq = ++this.searchSeq
      this.loading = true
      this.error = false

      const url = `/admin/api/v2/media/search?q=${encodeURIComponent(query)}&language=${this.lang}&status=draft,ready,published&type=${this.allowedMediaTypes.join()}`

      fetch(url)
        .then(resp => {
          if(!resp.ok) throw new Error(`search failed: ${resp.status}`)
          return resp.json()
        })
        .then(resp => {
          if(seq !== this.searchSeq) return // a newer search superseded this one

          let data = resp.data || []
          if(this.format == 'external') {
            data = data.filter(item => item.type == 'external')
          }

          this.srcItems = data
          this.loading = false
        })
        .catch(() => {
          if(seq !== this.searchSeq) return

          this.error = true
          this.loading = false
          this.srcItems = []
        })
    },

    getItems(items) {
      const ids = (items || []).map(p => p.id)

      if(!ids.length) {
        this.dstItems = []
        return
      }

      fetch(`/admin/api/v2/media/get-multiple?language=${this.lang}&status=draft,ready,published`, {method: 'POST', body: JSON.stringify({data: ids})})
      .then(function(resp) {
        return resp.json()
      })
      .then(resp => {
        // get-multiple does not guarantee order, so restore the saved
        // ordering (position) by following the incoming id list.
        const byId = {}
        for(const item of (resp.data || [])) {
          byId[item.id] = item
        }
        this.dstItems = ids.map(id => byId[id]).filter(Boolean)
      })
    },

    isAdded(id) {
      return this.addedIds.has(id)
    },

    addItem(item) {
      if(this.isAdded(item.id)) {
        return
      }

      this.dstItems.push(item)
      this.srcItems = this.srcItems.filter(i => i.id != item.id)
      this.emitUpdate()
    },

    removeItem(id) {
      this.dstItems = this.dstItems.filter(item => item.id != id)
      this.emitUpdate()
    },

    onReorder() {
      const orgLength = this.dstItems.length;
      this.dstItems = this.$_.uniqBy(this.dstItems, (item) => item.id)

      if(orgLength != this.dstItems.length) {
        this.$root.app.showAlert('Adding duplicate items to a manual category is not allowed', 'danger')
      }

      this.emitUpdate()
    },

    emitUpdate() {
      this.$emit('update', this.dstItems)
    },

    itemTitle(item) {
      return (item.meta && item.meta.title) ? item.meta.title : 'Untitled'
    },

    prettyType(type) {
      if(!type) return ''
      return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    },

    typeIcon(type) {
      return TYPE_ICONS[type] || 'photo-video'
    }
  },

  created() {
    this.debouncedSearch = this.$_.debounce(this.search, 300)
    this.setAllowedMediaTypes(this.assetFormat)
    this.getItems(this.items)
  },

  beforeDestroy() {
    if(this.debouncedSearch) {
      this.debouncedSearch.cancel()
    }
  }

}
</script>

<style scoped>
.manual-category {
  margin-top: 1.8rem;
}

.mc-pane {
  display: flex;
  flex-direction: column;
  height: 560px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
}

.mc-pane__header {
  display: flex;
  align-items: center;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.mc-pane__header-icon {
  color: #0090D9;
  margin-right: 0.6rem;
}

.mc-pane__title {
  font-weight: 600;
  letter-spacing: 0.3px;
  color: rgba(255, 255, 255, 0.9);
}

.mc-count {
  margin-left: auto;
  background: #005885;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.mc-search {
  padding: 0.75rem 0.75rem 0.5rem;
}

.mc-list-wrap {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0.75rem 0.75rem;
}

.mc-list {
  min-height: 100%;
}

.mc-list--empty {
  display: flex;
}

/* Rows */
.mc-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.65rem;
  margin-bottom: 0.4rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid transparent;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.mc-item--src {
  cursor: pointer;
}

.mc-item--src:hover {
  background: rgba(0, 144, 217, 0.12);
  border-color: rgba(0, 144, 217, 0.35);
}

.mc-item--added {
  opacity: 0.45;
  cursor: default;
}

.mc-item--added:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: transparent;
}

.mc-item__type-icon {
  color: rgba(255, 255, 255, 0.4);
  width: 16px;
  flex: 0 0 16px;
}

.mc-item__body {
  flex: 1;
  min-width: 0;
}

.mc-item__title {
  color: #E5E9EC;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mc-item__type {
  font-size: 0.72rem;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

.mc-item__action {
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0090D9;
  opacity: 0;
  transition: opacity 0.12s ease;
  white-space: nowrap;
}

.mc-item--src:hover .mc-item__action {
  opacity: 1;
}

.mc-item__action--added {
  color: rgba(120, 200, 140, 0.9);
  opacity: 1;
}

/* Placed (right pane) rows */
.mc-item__handle {
  color: rgba(255, 255, 255, 0.3);
  cursor: grab;
}

.mc-item__handle:active {
  cursor: grabbing;
}

.mc-item__pos {
  flex: 0 0 1.5rem;
  text-align: center;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.45);
}

.mc-item__remove {
  flex: 0 0 auto;
  padding: 0.15rem 0.4rem;
  color: rgba(255, 255, 255, 0.4);
}

.mc-item__remove:hover {
  color: #F35958;
}

/* States */
.mc-state {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem 1rem;
  color: rgba(255, 255, 255, 0.45);
}

.mc-state__icon {
  font-size: 1.6rem;
  margin-bottom: 0.6rem;
  color: rgba(255, 255, 255, 0.2);
}

.mc-state--error {
  color: rgba(243, 89, 88, 0.85);
}

.mc-state--error .mc-state__icon {
  color: rgba(243, 89, 88, 0.6);
}

.mc-state--drop {
  width: 100%;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 10px;
}

.mc-state small {
  margin-top: 0.35rem;
  opacity: 0.7;
}

/* Drag feedback */
.mc-item.sortable-ghost {
  opacity: 0.4;
  background: rgba(0, 144, 217, 0.18);
  border-color: rgba(0, 144, 217, 0.5);
}

.mc-item.sortable-chosen {
  background: rgba(0, 144, 217, 0.12);
}
</style>
